import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getUrl, remove, uploadData } from "aws-amplify/storage";
import { IconUpload } from "@tabler/icons-react";
import {
  AlertTriangle,
  CheckCircle2,
  CircleCheckBig,
  ChevronLeft,
  ArrowLeftToLine,
  ChevronRight,
  CreditCard,
  FileSignature,
  FileText,
  HeartPulse,
  Loader2,
  Bus,
  Trash2,
} from "lucide-react";
import { useEnrollmentProgress } from "@triedge/bridge/hooks";
import {
  useAddAdmissionsDocumentMutation,
  useGetAdmissionsApplicationQuery,
  useGetApplicantApplicationsQuery,
  useGetSettingsModuleConfigQuery,
  useUpdateAdmissionsEnrollmentProgressMutation,
  useUpdateAdmissionsMedicalRecordMutation,
  useUpsertEnrollmentStepDataMutation,
} from "../../state/domainRouterApi";
import { SupportErrorMessage } from "../../components/overlays/EngineeringSupportBanner";
import { useShellProfile } from "../../state/useShellProfile";
import { parseSettingsState } from "../../settings/settingsState";
import { useSchoolPublicInfo } from "../../internal-owned/school-settings-v2/hooks/useSchoolPublicInfo";
import { appendTuitionContractAcceptancePage } from "../../internal-owned/school-admissions-v2-1/utils/tuitionContractPdf";
import {
  buildTuitionContractSnapshot,
  formatTuitionContractCurrencyLabel as formatCurrencyLabel,
  resolveCanonicalTuitionContractSigningTerms,
  resolveCompatibleTuitionContractPaymentMethod,
  type TuitionContractPaymentMethod,
  type TuitionContractSnapshot,
} from "../../internal-owned/school-admissions-v2-1/utils/tuitionContractSnapshot";
import { createHostedPaymentSession } from "../../utils/payments";
import { isEnrollmentTuitionEligible } from "../enrollmentEligibility";
import {
  applyEnrollmentProgressCompletion,
  buildEnrollmentIntakeLink,
  buildEnrollmentProgressSnapshot,
  isEnrollmentRegistrationFormComplete,
  readEnrollmentProgressFlag,
  resolveEnrollmentTargetAction,
  type EnrollmentProgressOverrideMap,
  type EnrollmentProgressStepKey,
} from "../enrollmentProgressSource";
import { resolveSignatureCanvasPoint } from "../signatureCanvasGeometry";

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const readText = (value: unknown): string => String(value ?? "").trim();

const readBool = (value: unknown): boolean => Boolean(value);

const tuitionContractPaymentMethodLabels: Record<
  TuitionContractPaymentMethod,
  string
> = {
  full: "Payment in full",
  electronic: "Equal electronic payments",
  post_dated_checks: "Equal post-dated checks",
  third_party_checks: "Third-party checks",
};

const collectErrorMessages = (error: unknown, depth = 0): string[] => {
  if (depth > 4 || error == null) {
    return [];
  }
  if (error instanceof Error) {
    const nested = error as Error & {
      originalError?: unknown;
      cause?: unknown;
      errors?: unknown[];
    };
    return [
      error.message,
      ...collectErrorMessages(nested.originalError, depth + 1),
      ...collectErrorMessages(nested.cause, depth + 1),
      ...(Array.isArray(nested.errors)
        ? nested.errors.flatMap((entry) =>
            collectErrorMessages(entry, depth + 1),
          )
        : []),
    ];
  }
  if (typeof error === "object") {
    const record = error as Record<string, unknown>;
    return [
      String(record.message ?? ""),
      ...collectErrorMessages(record.originalError, depth + 1),
      ...collectErrorMessages(record.cause, depth + 1),
      ...(Array.isArray(record.errors)
        ? record.errors.flatMap((entry) =>
            collectErrorMessages(entry, depth + 1),
          )
        : []),
    ];
  }
  return [String(error)];
};

const isEnrollmentStepDataCompatibilityError = (error: unknown): boolean => {
  const combinedMessage = collectErrorMessages(error)
    .filter(Boolean)
    .join(" | ")
    .toLowerCase();
  if (!combinedMessage.includes("upsertenrollmentstepdata")) {
    return false;
  }
  return (
    combinedMessage.includes("fieldundefined") ||
    combinedMessage.includes("cannot query field") ||
    combinedMessage.includes("unknown field") ||
    combinedMessage.includes("is unavailable") ||
    combinedMessage.includes("validation error")
  );
};

const isBridgeNotConfiguredError = (error: unknown): boolean => {
  const combinedMessage = collectErrorMessages(error)
    .filter(Boolean)
    .join(" | ")
    .toLowerCase();
  return (
    combinedMessage.includes("bridge not configured") ||
    combinedMessage.includes("cannot update: bridge not configured") ||
    combinedMessage.includes("cannot complete: bridge not configured") ||
    combinedMessage.includes("not_configured")
  );
};

const toUnixSeconds = (value: Date): number =>
  Math.floor(value.getTime() / 1000);

const parseUnixSeconds = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }
  return null;
};

const formatUnixDate = (value: unknown): string => {
  const unix = parseUnixSeconds(value);
  if (unix == null || unix <= 0) return "TBD";
  return new Date(unix * 1000).toLocaleDateString();
};

const isStoragePath = (value: string): boolean =>
  /^(uploads|admissions)\//i.test(value);

const B6T_FORM_DOWNLOAD_HREF = "/B6T.pdf";
const B6T_UPLOAD_STORAGE_SEGMENT = "b6t-transport-form";
const TUITION_CONTRACT_UPLOAD_STORAGE_SEGMENT = "tuition-contract";
const SETTINGS_MODULE_KEY = "staff_settings";

const toSafeToken = (value: string): string =>
  String(value ?? "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);

const buildEnrollmentB6tUploadPath = (input: {
  applicationId: string;
  ownerId: string;
  childName: string;
  sourceFileName: string;
}): string => {
  const extensionMatch = input.sourceFileName.match(/\.[^.]+$/);
  const extension = extensionMatch?.[0] ?? "";
  const timestamp = Date.now();
  const safeOwner = toSafeToken(input.ownerId) || "unknown";
  const safeChildName = toSafeToken(input.childName) || "student";
  const safeBaseName =
    toSafeToken(input.sourceFileName.replace(/\.[^.]+$/, "")) || "b6t-form";
  return `uploads/admissions/${input.applicationId}/${safeOwner}/${B6T_UPLOAD_STORAGE_SEGMENT}/${timestamp}-${safeChildName}-${safeBaseName}${extension}`;
};

const buildEnrollmentTuitionContractUploadPath = (input: {
  applicationId: string;
  ownerId: string;
  familyName: string;
  fileStem: string;
}): string => {
  const timestamp = Date.now();
  const safeOwner = toSafeToken(input.ownerId) || "unknown";
  const safeFamilyName = toSafeToken(input.familyName) || "family";
  const safeFileStem = toSafeToken(input.fileStem) || "tuition-contract";
  return `uploads/admissions/${input.applicationId}/${safeOwner}/${TUITION_CONTRACT_UPLOAD_STORAGE_SEGMENT}/${timestamp}-${safeFamilyName}-${safeFileStem}.pdf`;
};

const statusLabel = (value: string): string =>
  value.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());

const resolveStatusChipLabel = (status: string): string => {
  const normalized = readText(status).toLowerCase();
  if (!normalized) return "";

  if (
    normalized === "submitted" ||
    normalized === "applied" ||
    normalized === "new"
  ) {
    return "Applied";
  }
  if (
    normalized === "in_progress" ||
    normalized === "in-progress" ||
    normalized === "in progress" ||
    normalized === "under_review" ||
    normalized === "pending"
  ) {
    return "In-Progress";
  }
  if (normalized === "scheduled" || normalized === "interview") {
    return "Scheduled";
  }
  if (
    normalized === "accepted" ||
    normalized === "accepted_pending_enrollment"
  ) {
    return "Accepted";
  }
  if (normalized === "enrolled") {
    return "Enrolled";
  }
  if (normalized === "waitlisted") {
    return "Waitlisted";
  }
  if (normalized === "denied" || normalized === "rejected") {
    return "Denied";
  }
  if (normalized === "archived") {
    return "Archived";
  }

  return statusLabel(status);
};

const STATUS_CHIP_CLASS_BY_LABEL: Record<string, string> = {
  Applied: "bg-aquamarine/10 text-aquamarine",
  "In-Progress": "bg-primary/10 text-primary",
  Scheduled: "bg-vibrant-coral/10 text-vibrant-coral",
  Accepted: "bg-jungle/10 text-jungle",
  Enrolled: "bg-primary text-white",
  Waitlisted: "bg-amber-100 text-amber-600",
  Denied: "bg-vibrant-coral text-white",
  Archived: "bg-gray-500 text-white",
};

const EnrollmentStatusBadge = ({ status }: { status: string }) => {
  const displayStatus = resolveStatusChipLabel(status);
  const classes =
    STATUS_CHIP_CLASS_BY_LABEL[displayStatus] || "bg-gray-100 text-irongrey/60";
  return (
    <span
      className={`inline-flex items-center rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${classes}`}
    >
      {displayStatus}
    </span>
  );
};

const STEPS = [
  {
    id: 1,
    key: "acceptance" as const,
    title: "Acceptance Letter",
    desc: "Review and acknowledge acceptance details",
    icon: FileText,
    progressKey: "step1AcceptanceLetter" as const,
  },
  {
    id: 2,
    key: "registration" as const,
    title: "Registration Form",
    desc: "Complete full registration intake form",
    icon: FileSignature,
    progressKey: "step2RegistrationForm" as const,
  },
  {
    id: 3,
    key: "contract" as const,
    title: "Tuition Contract",
    desc: "Review terms and record contract acceptance",
    icon: FileSignature,
    progressKey: "step4TuitionContract" as const,
  },
  {
    id: 4,
    key: "medical" as const,
    title: "Medical Information",
    desc: "Update medical details and immunization status",
    icon: HeartPulse,
    progressKey: "step6ImmunizationRecords" as const,
  },
  {
    id: 5,
    key: "b6t" as const,
    title: "Transportation (B6T)",
    desc: "Download the B6T Transport Form PDF",
    icon: Bus,
    progressKey: "step3B6TForm" as const,
  },
  {
    id: 6,
    key: "payment" as const,
    title: "Registration Payment",
    desc: "Pay registration fee via Stripe",
    icon: CreditCard,
    progressKey: "step5Payment" as const,
  },
];

type EnrollmentStepKey = (typeof STEPS)[number]["key"];

type EnrollmentTarget = {
  applicationId: string;
  applicationCode: string;
  familyName: string;
  childApplicationId: string;
  childName: string;
  childStatus: string;
  enrollmentProgress: Record<string, unknown>;
  enrollmentDeadline?: number | null;
};

const isAllEnrollmentStepsComplete = (
  rawProgress: Record<string, unknown>,
): boolean => {
  const progress = buildEnrollmentProgressSnapshot(rawProgress);

  return STEPS.every((step) => {
    if (step.key === "payment") {
      return readEnrollmentProgressFlag(progress.step5Payment);
    }
    return readEnrollmentProgressFlag(progress[step.progressKey]);
  });
};

const getChecklistActionLabel = (
  stepKey: EnrollmentStepKey,
  done: boolean,
): string => {
  if (done) return "View / Edit";
  if (stepKey === "acceptance") return "View";
  if (stepKey === "b6t") return "Upload";
  if (stepKey === "contract") return "Sign";
  if (stepKey === "medical") return "Complete Form";
  if (stepKey === "payment") return "Pay";
  return "Resume";
};

const resolveStep = (
  candidate: string | null,
): EnrollmentStepKey | "checklist" => {
  const normalized = readText(candidate).toLowerCase();
  if (normalized === "checklist") return "checklist";
  if (normalized === "b6t") return "checklist";
  if (STEPS.some((step) => step.key === normalized)) {
    return normalized as EnrollmentStepKey;
  }
  return "checklist";
};

const ChecklistCompleteBadge = () => (
  <span className="inline-flex w-[131px] items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700">
    <CircleCheckBig className="h-3.5 w-3.5" />
    Complete
  </span>
);

const DigitalSignaturePad = ({
  label,
  value,
  onChange,
  onBeginEdit,
  required = false,
  testId,
}: {
  label: string;
  value: string | null;
  onChange: (nextValue: string | null) => void;
  onBeginEdit?: () => void;
  required?: boolean;
  testId?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.strokeStyle = "#404f4d";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!value) return;
    const image = new Image();
    image.src = value;
    image.onload = () => {
      ctx.drawImage(image, 0, 0);
    };
  }, [value]);

  const getPos = (event: ReactMouseEvent | ReactTouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const resolvePoint = (clientX: number, clientY: number) =>
      resolveSignatureCanvasPoint({
        clientX,
        clientY,
        rect,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
      });

    if ("touches" in event) {
      const touch = event.touches[0];
      if (!touch) {
        return { x: 0, y: 0 };
      }
      return resolvePoint(touch.clientX, touch.clientY);
    }
    return resolvePoint(event.clientX, event.clientY);
  };

  const startDrawing = (event: ReactMouseEvent | ReactTouchEvent) => {
    onBeginEdit?.();
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = getPos(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (event: ReactMouseEvent | ReactTouchEvent) => {
    if (!isDrawing) return;
    if ("touches" in event) event.preventDefault();
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = getPos(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    onBeginEdit?.();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-semibold text-irongrey">
        {label}
        {required ? " *" : ""}
      </p>
      <div className="relative overflow-hidden rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 transition-colors hover:border-primary/40">
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          data-testid={testId}
          className="h-44 w-full cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!value && !isDrawing ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-bold text-irongrey/25">
            Sign here
          </div>
        ) : null}
        <button
          type="button"
          onClick={clear}
          className="absolute right-2 top-2 inline-flex items-center rounded-lg border border-gray-200 bg-white p-1.5 text-irongrey/50 hover:bg-red-50 hover:text-red-700"
          title="Clear Signature"
          aria-label="Clear Signature"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <p className="text-right text-[10px] text-irongrey/50">
        Use mouse, trackpad, or touch to sign
      </p>
    </div>
  );
};

const MedicalEditor = ({
  initialMedical,
  onSave,
  saving,
}: {
  initialMedical: Record<string, unknown>;
  onSave: (payload: {
    allergies: string[];
    chronicConditions: string[];
    medications: string[];
    immunizationStatus: "up_to_date" | "partial" | "exempt" | "not_provided";
    emergencyNotes: string;
  }) => Promise<void>;
  saving: boolean;
}) => {
  const [allergies, setAllergies] = useState(
    asArray<string>(initialMedical.allergies).join(", "),
  );
  const [chronicConditions, setChronicConditions] = useState(
    asArray<string>(initialMedical.chronicConditions).join(", "),
  );
  const [medications, setMedications] = useState(
    asArray<string>(initialMedical.medications).join(", "),
  );
  const [immunizationStatus, setImmunizationStatus] = useState<
    "up_to_date" | "partial" | "exempt" | "not_provided"
  >(
    (readText(initialMedical.immunizationStatus).toLowerCase() as
      | "up_to_date"
      | "partial"
      | "exempt"
      | "not_provided") || "not_provided",
  );
  const [emergencyNotes, setEmergencyNotes] = useState(
    readText(initialMedical.emergencyNotes),
  );

  const splitCsv = (value: string): string[] =>
    value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);

  const allergyList = splitCsv(allergies);
  const medicationList = splitCsv(medications);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-semibold text-irongrey space-y-1">
          Allergies
          <input
            type="text"
            value={allergies}
            onChange={(event) => setAllergies(event.target.value)}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            placeholder="nuts, dairy"
          />
        </label>
        <label className="text-sm font-semibold text-irongrey space-y-1">
          Current Medications
          <input
            type="text"
            value={medications}
            onChange={(event) => setMedications(event.target.value)}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            placeholder="medication names"
          />
        </label>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-semibold text-irongrey space-y-1">
          Chronic Conditions
          <input
            type="text"
            value={chronicConditions}
            onChange={(event) => setChronicConditions(event.target.value)}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            placeholder="asthma"
          />
        </label>
        <label className="text-sm font-semibold text-irongrey space-y-1">
          Immunization Status
          <select
            value={immunizationStatus}
            onChange={(event) =>
              setImmunizationStatus(
                event.target.value as
                  | "up_to_date"
                  | "partial"
                  | "exempt"
                  | "not_provided",
              )
            }
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="up_to_date">Up to date</option>
            <option value="partial">Partial</option>
            <option value="exempt">Exempt</option>
            <option value="not_provided">Not provided</option>
          </select>
        </label>
      </div>
      <label className="text-sm font-semibold text-irongrey space-y-1 block">
        Emergency Notes
        <textarea
          value={emergencyNotes}
          onChange={(event) => setEmergencyNotes(event.target.value)}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm min-h-[96px]"
          placeholder="Any emergency instructions"
        />
      </label>
      <div className="mt-4 overflow-hidden rounded-lg border">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-1/4 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Allergies
              </th>
              <th className="w-1/4 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Medications
              </th>
              <th className="w-1/2 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Emergency Notes
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <tr>
              <td className="align-top px-4 py-2 text-sm text-gray-900">
                {allergyList.length ? (
                  <div className="flex flex-wrap gap-1">
                    {allergyList.map((entry, index) => (
                      <span
                        key={`${entry}-${index}`}
                        className="inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
                      >
                        {entry}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs italic text-gray-400">None</span>
                )}
              </td>
              <td className="align-top px-4 py-2 text-sm text-gray-900">
                {medicationList.length ? (
                  <div className="flex flex-wrap gap-1">
                    {medicationList.map((entry, index) => (
                      <span
                        key={`${entry}-${index}`}
                        className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                      >
                        {entry}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs italic text-gray-400">None</span>
                )}
              </td>
              <td className="whitespace-pre-wrap align-top px-4 py-2 text-sm text-gray-900">
                {emergencyNotes || (
                  <span className="text-xs italic text-gray-400">None</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={saving}
          onClick={() =>
            onSave({
              allergies: splitCsv(allergies),
              chronicConditions: splitCsv(chronicConditions),
              medications: splitCsv(medications),
              immunizationStatus,
              emergencyNotes: emergencyNotes.trim(),
            })
          }
          className="inline-flex items-center gap-2 rounded-xl bg-jungle px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          Save Medical Information
        </button>
      </div>
    </div>
  );
};

export function PortalEnrollmentStatusPage() {
  const { profile } = useShellProfile();
  const applicantId = profile?.userSub ?? null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [acceptanceNotes, setAcceptanceNotes] = useState("");
  const [contractSignerName, setContractSignerName] = useState("");
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractSignature, setContractSignature] = useState<string | null>(
    null,
  );
  const [contractPaymentMethod, setContractPaymentMethod] =
    useState<TuitionContractPaymentMethod>("full");
  const [contractPaymentAmount, setContractPaymentAmount] = useState("");
  const [contractInstallmentCount, setContractInstallmentCount] = useState("");
  const [contractInstallmentAmount, setContractInstallmentAmount] =
    useState("");
  const [contractInstallmentStartDate, setContractInstallmentStartDate] =
    useState("");
  const [contractInstallmentEndDate, setContractInstallmentEndDate] =
    useState("");
  const [contractThirdPartyName, setContractThirdPartyName] = useState("");
  const isContractDraftDirtyRef = useRef(false);
  const lastContractSyncTokenRef = useRef("");
  const activeContractChildRef = useRef("");
  const isAcceptanceDraftDirtyRef = useRef(false);
  const lastAcceptanceSyncTokenRef = useRef("");
  const activeAcceptanceChildRef = useRef("");
  const b6tUploadInputRef = useRef<HTMLInputElement>(null);
  const [b6tUploading, setB6tUploading] = useState(false);
  const [b6tUploadError, setB6tUploadError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [openingAcceptanceDocId, setOpeningAcceptanceDocId] = useState<
    string | null
  >(null);
  const [openingContractDocId, setOpeningContractDocId] = useState<
    string | null
  >(null);
  const [paymentCheckoutLaunching, setPaymentCheckoutLaunching] =
    useState(false);
  const [paymentCheckoutError, setPaymentCheckoutError] = useState<
    string | null
  >(null);
  const [completedStepOverrides, setCompletedStepOverrides] =
    useState<EnrollmentProgressOverrideMap>({});

  const applicationsQuery = useGetApplicantApplicationsQuery(
    { applicantId: applicantId ?? "", limit: 50 },
    { skip: !applicantId },
  );
  const settingsModuleQuery = useGetSettingsModuleConfigQuery({
    moduleKey: SETTINGS_MODULE_KEY,
  });

  const enrollmentTargets = useMemo(() => {
    const items = applicationsQuery.data?.items ?? [];
    const targets: EnrollmentTarget[] = [];

    for (const item of items) {
      const application = asRecord(item);
      const applicationId = readText(application.id);
      if (!applicationId) continue;
      const applicationCode = readText(application.code) || "Code pending";
      const familyName =
        readText(asRecord(application.applicant).familyName) || "Family";
      const applicationStatus = readText(application.status).toLowerCase();
      const children = asArray<Record<string, unknown>>(application.children);

      for (const rawChild of children) {
        const child = asRecord(rawChild);
        const childApplicationId = readText(child.id);
        if (!childApplicationId) continue;
        const childStatus = (
          readText(child.status) ||
          applicationStatus ||
          "submitted"
        ).toLowerCase();
        const eligible = isEnrollmentTuitionEligible({
          applicationStatus,
          childStatus,
        });
        if (!eligible) continue;
        const childInfo = asRecord(child.childInfo);
        const childName =
          `${readText(childInfo.firstName)} ${readText(childInfo.lastName)}`.trim() ||
          "Student";
        const childProgress = asRecord(child.enrollmentProgress);

        targets.push({
          applicationId,
          applicationCode,
          familyName,
          childApplicationId,
          childName,
          childStatus,
          enrollmentProgress: childProgress,
          enrollmentDeadline: parseUnixSeconds(child.enrollmentDeadline),
        });
      }
    }

    return targets;
  }, [applicationsQuery.data?.items]);

  const shouldRenderTargetList = enrollmentTargets.length > 0;

  const selectedApplicationId = readText(searchParams.get("applicationId"));
  const selectedChildApplicationId = readText(
    searchParams.get("childApplicationId"),
  );
  const selectedStep = resolveStep(searchParams.get("step"));

  const selectedTarget = useMemo(() => {
    const explicit = enrollmentTargets.find(
      (entry) =>
        entry.applicationId === selectedApplicationId &&
        entry.childApplicationId === selectedChildApplicationId,
    );
    if (explicit) {
      return explicit;
    }
    if (selectedApplicationId && selectedChildApplicationId) {
      return {
        applicationId: selectedApplicationId,
        applicationCode: "Code pending",
        familyName: "Family",
        childApplicationId: selectedChildApplicationId,
        childName: "Student",
        childStatus: "accepted_pending_enrollment",
        enrollmentProgress: {},
        enrollmentDeadline: null,
      };
    }
    if (!enrollmentTargets.length) return null;
    return enrollmentTargets[0] ?? null;
  }, [enrollmentTargets, selectedApplicationId, selectedChildApplicationId]);

  const applicationDetailsQuery = useGetAdmissionsApplicationQuery(
    selectedTarget?.applicationId ?? "",
    {
      skip: !selectedTarget?.applicationId,
    },
  );
  const { refetch: refetchApplicationDetails } = applicationDetailsQuery;

  const selectedChild = useMemo(() => {
    const appRecord = asRecord(applicationDetailsQuery.data);
    const children = asArray<Record<string, unknown>>(appRecord.children);
    return asRecord(
      children.find(
        (entry) => readText(entry.id) === selectedTarget?.childApplicationId,
      ),
    );
  }, [applicationDetailsQuery.data, selectedTarget?.childApplicationId]);

  const selectedListProgress = asRecord(selectedTarget?.enrollmentProgress);
  const selectedDetailProgress = asRecord(selectedChild.enrollmentProgress);
  const selectedProgress = buildEnrollmentProgressSnapshot(
    selectedListProgress,
    selectedDetailProgress,
    selectedTarget
      ? completedStepOverrides[selectedTarget.childApplicationId]
      : null,
  );
  const selectedRegistrationFormComplete =
    isEnrollmentRegistrationFormComplete(selectedProgress);

  const selectedStepData = asRecord(selectedChild.enrollmentStepData);
  const selectedAcceptanceStepData = asRecord(
    selectedStepData.acceptanceLetter,
  );
  const selectedTuitionContractStepData = asRecord(
    selectedStepData.tuitionContract,
  );
  const schoolPublicInfo = useSchoolPublicInfo();
  const settingsState = useMemo(
    () => parseSettingsState(asRecord(settingsModuleQuery.data).payload),
    [settingsModuleQuery.data],
  );
  const selectedApplicationRecord = asRecord(applicationDetailsQuery.data);
  const selectedTuitionPaymentPlan = asRecord(
    asRecord(selectedApplicationRecord.tuitionConfiguration).paymentPlan,
  );
  const canonicalTuitionContractSigningTerms = useMemo(
    () =>
      resolveCanonicalTuitionContractSigningTerms(
        selectedApplicationRecord.tuitionConfiguration,
      ),
    [selectedApplicationRecord.tuitionConfiguration],
  );
  const admissionSchoolYear =
    schoolPublicInfo.academicYearLabel ||
    `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;
  const selectedApplicationDocuments = useMemo(
    () =>
      asArray<Record<string, unknown>>(
        asRecord(applicationDetailsQuery.data).documents,
      ),
    [applicationDetailsQuery.data],
  );
  const acceptanceDocuments = useMemo(() => {
    if (!selectedTarget) return [] as Array<Record<string, unknown>>;
    const matches = selectedApplicationDocuments.filter((document) => {
      const category = readText(document.category).toLowerCase();
      const fileName = readText(document.fileName).toLowerCase();
      const childIds = asArray<string>(document.childApplicationIds).map(
        (entry) => readText(entry),
      );
      const matchesStudent =
        childIds.length === 0 ||
        childIds.includes(selectedTarget.childApplicationId);
      const acceptanceHint =
        category.includes("acceptance") || fileName.includes("acceptance");
      return matchesStudent && acceptanceHint;
    });
    const acceptanceStepDocumentResourceId = readText(
      selectedAcceptanceStepData.documentResourceId,
    );
    const acceptanceStepDocumentId = readText(
      selectedAcceptanceStepData.documentId,
    );
    const isStepLinkedDocument = (entry: Record<string, unknown>): boolean => {
      const entryResourceId = readText(entry.resourceId);
      const entryId = readText(entry.id);
      return (
        Boolean(
          acceptanceStepDocumentResourceId &&
            entryResourceId === acceptanceStepDocumentResourceId,
        ) ||
        Boolean(
          acceptanceStepDocumentId && entryId === acceptanceStepDocumentId,
        )
      );
    };

    if (acceptanceStepDocumentResourceId) {
      const alreadyIncluded = matches.some((entry) =>
        isStepLinkedDocument(entry),
      );
      if (!alreadyIncluded) {
        matches.unshift({
          id:
            acceptanceStepDocumentId ||
            `acceptance-step-${selectedTarget.childApplicationId}`,
          category: "acceptance_letter",
          fileName: "Acceptance letter",
          resourceId: acceptanceStepDocumentResourceId,
          childApplicationIds: [selectedTarget.childApplicationId],
          uploadedAt: selectedAcceptanceStepData.reviewedAt,
        });
      }
    }
    matches.sort((left, right) => {
      const leftTime =
        parseUnixSeconds(left.uploadedAt) ??
        parseUnixSeconds(left.createdAt) ??
        parseUnixSeconds(left.updatedAt) ??
        0;
      const rightTime =
        parseUnixSeconds(right.uploadedAt) ??
        parseUnixSeconds(right.createdAt) ??
        parseUnixSeconds(right.updatedAt) ??
        0;
      return rightTime - leftTime;
    });
    return matches;
  }, [
    selectedApplicationDocuments,
    selectedTarget,
    selectedAcceptanceStepData,
  ]);
  const tuitionContractDocuments = useMemo(() => {
    if (!selectedTarget) return [] as Array<Record<string, unknown>>;
    const matches = selectedApplicationDocuments.filter((document) => {
      const category = readText(document.category).toLowerCase();
      const fileName = readText(document.fileName).toLowerCase();
      const childIds = asArray<string>(document.childApplicationIds).map(
        (entry) => readText(entry),
      );
      const matchesStudent =
        childIds.length === 0 ||
        childIds.includes(selectedTarget.childApplicationId);
      const contractHint =
        category.includes("contract") ||
        fileName.includes("tuition-contract") ||
        fileName.includes("tuition contract");
      return matchesStudent && contractHint;
    });

    const contractDocumentResourceId = readText(
      selectedTuitionContractStepData.documentResourceId,
    );
    const contractDocumentId = readText(
      selectedTuitionContractStepData.documentId,
    );
    const isStepLinkedDocument = (entry: Record<string, unknown>): boolean => {
      const entryResourceId = readText(entry.resourceId);
      const entryId = readText(entry.id);
      return (
        Boolean(
          contractDocumentResourceId &&
            entryResourceId === contractDocumentResourceId,
        ) || Boolean(contractDocumentId && entryId === contractDocumentId)
      );
    };

    if (contractDocumentResourceId) {
      const alreadyIncluded = matches.some((entry) =>
        isStepLinkedDocument(entry),
      );
      if (!alreadyIncluded) {
        matches.unshift({
          id:
            contractDocumentId ||
            `tuition-contract-step-${selectedTarget.childApplicationId}`,
          category: "tuition_contract",
          fileName: "Tuition contract",
          resourceId: contractDocumentResourceId,
          childApplicationIds: [selectedTarget.childApplicationId],
          uploadedAt: selectedTuitionContractStepData.signedAt,
        });
      }
    }

    matches.sort((left, right) => {
      const leftTime =
        parseUnixSeconds(left.uploadedAt) ??
        parseUnixSeconds(left.createdAt) ??
        parseUnixSeconds(left.updatedAt) ??
        0;
      const rightTime =
        parseUnixSeconds(right.uploadedAt) ??
        parseUnixSeconds(right.createdAt) ??
        parseUnixSeconds(right.updatedAt) ??
        0;
      return rightTime - leftTime;
    });
    return matches;
  }, [
    selectedApplicationDocuments,
    selectedTarget,
    selectedTuitionContractStepData,
  ]);
  const b6tDocuments = useMemo(() => {
    if (!selectedTarget) return [] as Array<Record<string, unknown>>;
    const matches = selectedApplicationDocuments.filter((document) => {
      const childIds = asArray<string>(document.childApplicationIds).map(
        (entry) => readText(entry),
      );
      const matchesStudent =
        childIds.length === 0 ||
        childIds.includes(selectedTarget.childApplicationId);
      const resourceId = readText(document.resourceId).toLowerCase();
      const storageKey = readText(document.storageKey).toLowerCase();
      const fileName = readText(document.fileName).toLowerCase();
      const isB6tUpload =
        resourceId.includes(B6T_UPLOAD_STORAGE_SEGMENT) ||
        storageKey.includes(B6T_UPLOAD_STORAGE_SEGMENT) ||
        fileName.includes("b6t");
      return matchesStudent && isB6tUpload;
    });
    matches.sort((left, right) => {
      const leftTime =
        parseUnixSeconds(left.uploadedAt) ??
        parseUnixSeconds(left.createdAt) ??
        parseUnixSeconds(left.updatedAt) ??
        0;
      const rightTime =
        parseUnixSeconds(right.uploadedAt) ??
        parseUnixSeconds(right.createdAt) ??
        parseUnixSeconds(right.updatedAt) ??
        0;
      return rightTime - leftTime;
    });
    return matches;
  }, [selectedApplicationDocuments, selectedTarget]);
  const enrollmentProgress = useEnrollmentProgress();
  const [updateEnrollmentProgressMutation, updateEnrollmentProgressState] =
    useUpdateAdmissionsEnrollmentProgressMutation();
  const [updateMedicalRecordMutation, updateMedicalRecordState] =
    useUpdateAdmissionsMedicalRecordMutation();
  const [upsertEnrollmentStepDataMutation, upsertEnrollmentStepDataState] =
    useUpsertEnrollmentStepDataMutation();
  const [addAdmissionsDocument, addAdmissionsDocumentState] =
    useAddAdmissionsDocumentMutation();
  const isPersisting =
    enrollmentProgress.updating ||
    updateEnrollmentProgressState.isLoading ||
    updateMedicalRecordState.isLoading ||
    upsertEnrollmentStepDataState.isLoading ||
    addAdmissionsDocumentState.isLoading ||
    b6tUploading;

  const tuitionContractSnapshot =
    useMemo<TuitionContractSnapshot | null>(() => {
      return buildTuitionContractSnapshot({
        selectedTarget,
        application: selectedApplicationRecord,
        settingsState,
        admissionSchoolYear,
        schoolPublicInfo,
      });
    }, [
      selectedTarget,
      selectedApplicationRecord,
      settingsState,
      admissionSchoolYear,
      schoolPublicInfo,
    ]);

  useEffect(() => {
    const acceptance = asRecord(selectedStepData.acceptanceLetter);
    const contract = selectedTuitionContractStepData;
    const childId = selectedTarget?.childApplicationId ?? "";
    const acceptanceNotesToken = readText(acceptance.notes);
    const acceptanceSyncToken = `${childId}|${acceptanceNotesToken}`;
    if (
      activeAcceptanceChildRef.current !== childId ||
      (!isAcceptanceDraftDirtyRef.current &&
        lastAcceptanceSyncTokenRef.current !== acceptanceSyncToken)
    ) {
      setAcceptanceNotes(acceptanceNotesToken);
      activeAcceptanceChildRef.current = childId;
      lastAcceptanceSyncTokenRef.current = acceptanceSyncToken;
      isAcceptanceDraftDirtyRef.current = false;
    }

    const contractSignerToken = readText(contract.signerFullName);
    const contractAcceptedToken = readBool(contract.termsAcknowledged);
    const contractSignatureToken = readText(
      contract.signatureToken ||
        contract.signatureDataUrl ||
        contract.signature,
    );
    const paymentMethodToken = resolveCompatibleTuitionContractPaymentMethod(
      contract.paymentMethod || contract.paymentPreference,
      canonicalTuitionContractSigningTerms?.installmentCount ??
        selectedTuitionPaymentPlan.installmentCount,
    );
    const paymentAmountToken =
      canonicalTuitionContractSigningTerms?.paymentAmountLabel ??
      readText(contract.paymentAmountLabel || contract.paymentAmount);
    const installmentCountToken =
      canonicalTuitionContractSigningTerms?.installmentCount ??
      readText(contract.installmentCount);
    const installmentAmountToken =
      canonicalTuitionContractSigningTerms?.installmentAmountLabel ??
      readText(contract.installmentAmountLabel || contract.installmentAmount);
    const installmentStartToken =
      canonicalTuitionContractSigningTerms?.installmentStartDate ??
      readText(contract.installmentStartDate);
    const installmentEndToken =
      canonicalTuitionContractSigningTerms?.installmentEndDate ??
      readText(contract.installmentEndDate);
    const thirdPartyNameToken = readText(contract.thirdPartyName);
    const contractSyncToken = `${childId}|${contractSignerToken}|${contractAcceptedToken ? 1 : 0}|${contractSignatureToken ? 1 : 0}|${paymentMethodToken}|${paymentAmountToken}|${installmentCountToken}|${installmentAmountToken}|${installmentStartToken}|${installmentEndToken}|${thirdPartyNameToken}`;
    if (
      activeContractChildRef.current !== childId ||
      (!isContractDraftDirtyRef.current &&
        lastContractSyncTokenRef.current !== contractSyncToken)
    ) {
      setContractSignerName(contractSignerToken);
      setContractAccepted(contractAcceptedToken);
      setContractSignature(contractSignatureToken || null);
      setContractPaymentMethod(paymentMethodToken);
      setContractPaymentAmount(paymentAmountToken);
      setContractInstallmentCount(installmentCountToken);
      setContractInstallmentAmount(installmentAmountToken);
      setContractInstallmentStartDate(installmentStartToken);
      setContractInstallmentEndDate(installmentEndToken);
      setContractThirdPartyName(thirdPartyNameToken);
      activeContractChildRef.current = childId;
      lastContractSyncTokenRef.current = contractSyncToken;
      isContractDraftDirtyRef.current = false;
    }
  }, [
    selectedStepData,
    selectedTuitionContractStepData,
    selectedTarget?.childApplicationId,
    canonicalTuitionContractSigningTerms,
    selectedTuitionPaymentPlan.installmentCount,
  ]);

  const paymentRecord = asRecord(selectedStepData.registrationFee);
  const registrationFee = schoolPublicInfo.registrationFee;
  const registrationFeeAmountCents = Math.max(
    0,
    Number(registrationFee?.amountCents ?? 0),
  );
  const registrationFeeAmountLabel =
    registrationFee?.amountLabel ||
    formatCurrencyLabel(registrationFeeAmountCents);
  const registrationFeeScopeLabel = registrationFee?.scopeLabel || "per child";
  const paymentStatusParam = readText(
    searchParams.get("payment_status"),
  ).toLowerCase();
  const paymentStatusFromUrl =
    paymentStatusParam === "paid" || paymentStatusParam === "success";
  const paymentCanceledFromUrl = paymentStatusParam === "cancel";
  const paymentStatusFromData =
    readBool(paymentRecord.paymentCompleted) ||
    readBool(paymentRecord.stripePaymentCompleted) ||
    readBool(paymentRecord.paid) ||
    Number(paymentRecord.amountPaidCents ?? 0) > 0 ||
    Boolean(readText(paymentRecord.transactionReference)) ||
    parseUnixSeconds(paymentRecord.paidAt) != null;
  const paymentStepDone =
    readEnrollmentProgressFlag(selectedProgress.step5Payment) ||
    paymentStatusFromData;
  const b6tStepDone =
    readEnrollmentProgressFlag(selectedProgress.step3B6TForm) ||
    b6tDocuments.length > 0;

  const completedCount = STEPS.filter((step) => {
    if (step.key === "payment") return paymentStepDone;
    if (step.key === "b6t") return b6tStepDone;
    if (step.key === "registration") return selectedRegistrationFormComplete;
    return readEnrollmentProgressFlag(selectedProgress[step.progressKey]);
  }).length;
  const progressPct = STEPS.length
    ? Math.round((completedCount / STEPS.length) * 100)
    : 0;
  const deadlineUnix = selectedTarget?.enrollmentDeadline;
  const daysRemaining =
    typeof deadlineUnix === "number"
      ? Math.ceil((deadlineUnix * 1000 - Date.now()) / 86400000)
      : null;
  const contractPaymentPreferenceLabel =
    tuitionContractPaymentMethodLabels[contractPaymentMethod];
  const contractPaymentAmountLabel =
    readText(contractPaymentAmount) ||
    formatCurrencyLabel(tuitionContractSnapshot?.totalObligationCents ?? 0);
  const contractPaymentUsesInstallments =
    Number(selectedTuitionPaymentPlan.installmentCount) > 1;
  const contractPaymentUsesThirdParty =
    contractPaymentMethod === "third_party_checks";
  const contractCanSave = Boolean(
    contractAccepted &&
      contractSignerName.trim() &&
      contractSignature &&
      tuitionContractSnapshot?.studentRows.length,
  );

  const syncSearchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const jumpToChecklist = () => syncSearchParams({ step: "checklist" });

  const jumpToStep = (step: EnrollmentStepKey) => {
    if (!selectedTarget) return;
    syncSearchParams({
      applicationId: selectedTarget.applicationId,
      childApplicationId: selectedTarget.childApplicationId,
      step,
    });
  };

  useEffect(() => {
    if (
      !paymentStatusFromUrl ||
      paymentStepDone ||
      !selectedTarget?.applicationId
    ) {
      return;
    }
    let cancelled = false;
    const timers = new Set<number>();

    const poll = async (attempt: number) => {
      if (cancelled) return;
      try {
        await refetchApplicationDetails();
      } catch {
        // Best-effort polling while webhook-backed payment sync settles.
      }
      if (cancelled || attempt >= 4) {
        return;
      }
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        void poll(attempt + 1);
      }, 2000);
      timers.add(timer);
    };

    void poll(0);

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [
    refetchApplicationDetails,
    paymentStatusFromUrl,
    paymentStepDone,
    selectedTarget?.applicationId,
  ]);

  const startRegistrationPaymentCheckout = async () => {
    if (!selectedTarget) {
      setPaymentCheckoutError(
        "Select a student before launching the registration payment.",
      );
      return;
    }
    if (registrationFeeAmountCents <= 0) {
      setPaymentCheckoutError(
        "Registration fee is not configured yet. Please contact the school office.",
      );
      return;
    }

    setPaymentCheckoutLaunching(true);
    setPaymentCheckoutError(null);

    const buildReturnUrl = (status: "success" | "cancel") => {
      const params = new URLSearchParams({
        applicationId: selectedTarget.applicationId,
        childApplicationId: selectedTarget.childApplicationId,
        step: "payment",
        payment_status: status,
      });
      return `${window.location.origin}/parent/portal/enrollment?${params.toString()}`;
    };

    try {
      const checkoutUrl = await createHostedPaymentSession({
        moduleId: "enrollment",
        operationKey: "enrollment_registration_checkout",
        mode: "payment",
        currency: "usd",
        successUrl: buildReturnUrl("success"),
        cancelUrl: buildReturnUrl("cancel"),
        customerEmail: profile?.email,
        customerReferenceId: profile?.userSub ?? undefined,
        description: "Parent enrollment registration payment",
        lineItems: [
          {
            name: "Enrollment registration fee",
            description:
              `${selectedTarget.childName} · ${registrationFeeAmountLabel} ${registrationFeeScopeLabel}`.trim(),
            quantity: 1,
            unitAmountCents: registrationFeeAmountCents,
          },
        ],
        metadata: {
          applicationId: selectedTarget.applicationId,
          childApplicationId: selectedTarget.childApplicationId,
          childName: selectedTarget.childName,
          actorSub: profile?.userSub ?? "",
          route: "/parent/portal/enrollment",
        },
      });
      window.location.assign(checkoutUrl);
    } catch (error) {
      const message =
        collectErrorMessages(error).find(Boolean) ||
        "Unable to launch registration payment.";
      setPaymentCheckoutError(message);
    } finally {
      setPaymentCheckoutLaunching(false);
    }
  };

  const markStepComplete = async (
    step: Exclude<EnrollmentProgressStepKey, "step2RegistrationForm">,
  ) => {
    if (!selectedTarget?.childApplicationId) return;
    try {
      await enrollmentProgress.updateProgress({
        childApplicationId: selectedTarget.childApplicationId,
        step,
        completed: true,
      });
    } catch (error) {
      if (!isBridgeNotConfiguredError(error)) {
        throw error;
      }
      await updateEnrollmentProgressMutation({
        childApplicationId: selectedTarget.childApplicationId,
        step,
        completed: true,
      }).unwrap();
    }
    setCompletedStepOverrides((previous) =>
      applyEnrollmentProgressCompletion(
        previous,
        selectedTarget.childApplicationId,
        step,
      ),
    );
  };

  const persistEnrollmentStepData = async (payload: {
    childApplicationId: string;
    step:
      | "step1AcceptanceLetter"
      | "step4TuitionContract"
      | "step3B6TForm"
      | "step5Payment";
    data: Record<string, unknown>;
  }): Promise<boolean> => {
    const stepDataKeyByStep: Record<typeof payload.step, string> = {
      step1AcceptanceLetter: "acceptanceLetter",
      step4TuitionContract: "tuitionContract",
      step3B6TForm: "transportationB6t",
      step5Payment: "registrationFee",
    };
    const didPersistRequestedDocumentPointers = (result: unknown): boolean => {
      const requested = asRecord(payload.data);
      const requestedDocumentId = readText(requested.documentId);
      const requestedDocumentResourceId = readText(
        requested.documentResourceId,
      );
      if (!requestedDocumentId && !requestedDocumentResourceId) {
        return true;
      }

      const childRecord = asRecord(result);
      const enrollmentStepData = asRecord(childRecord.enrollmentStepData);
      const persistedStepData = asRecord(
        enrollmentStepData[stepDataKeyByStep[payload.step]],
      );
      if (
        requestedDocumentId &&
        readText(persistedStepData.documentId) !== requestedDocumentId
      ) {
        return false;
      }
      if (
        requestedDocumentResourceId &&
        readText(persistedStepData.documentResourceId) !==
          requestedDocumentResourceId
      ) {
        return false;
      }
      return true;
    };

    try {
      const bridge = enrollmentProgress as unknown as {
        upsertStepData?: (value: typeof payload) => Promise<unknown>;
      };
      if (!bridge.upsertStepData) {
        throw new Error("Bridge not configured");
      }
      const result = await bridge.upsertStepData(payload);
      return didPersistRequestedDocumentPointers(result);
    } catch (error) {
      if (isBridgeNotConfiguredError(error)) {
        try {
          const result =
            await upsertEnrollmentStepDataMutation(payload).unwrap();
          return didPersistRequestedDocumentPointers(result);
        } catch (mutationError) {
          if (isEnrollmentStepDataCompatibilityError(mutationError)) {
            return false;
          }
          throw mutationError;
        }
      }
      if (isEnrollmentStepDataCompatibilityError(error)) {
        return false;
      }
      throw error;
    }
  };

  const openApplicationDocument = async (input: {
    document: Record<string, unknown>;
    fallbackId: string;
    setOpeningDocumentId: (value: string | null) => void;
    unavailableMessage: string;
    failedMessage: string;
  }) => {
    const resourceId = readText(input.document.resourceId);
    const documentId = readText(input.document.id) || input.fallbackId;
    if (!resourceId) {
      setToastMessage(input.unavailableMessage);
      return;
    }
    input.setOpeningDocumentId(documentId);
    try {
      if (/^https?:\/\//i.test(resourceId)) {
        window.open(resourceId, "_blank", "noopener,noreferrer");
        return;
      }
      if (!isStoragePath(resourceId)) {
        throw new Error(
          "Acceptance document preview is not available for this resource.",
        );
      }
      const signed = await getUrl({ path: resourceId });
      window.open(signed.url.toString(), "_blank", "noopener,noreferrer");
    } catch {
      setToastMessage(input.failedMessage);
    } finally {
      input.setOpeningDocumentId(null);
    }
  };

  const openAcceptanceDocument = async (
    document: Record<string, unknown>,
    index: number,
  ) =>
    openApplicationDocument({
      document,
      fallbackId: `acceptance-${index}`,
      setOpeningDocumentId: setOpeningAcceptanceDocId,
      unavailableMessage: "Acceptance document link is not available yet.",
      failedMessage: "Unable to open this acceptance document right now.",
    });

  const openTuitionContractDocument = async (
    document: Record<string, unknown>,
    index: number,
  ) =>
    openApplicationDocument({
      document,
      fallbackId: `tuition-contract-${index}`,
      setOpeningDocumentId: setOpeningContractDocId,
      unavailableMessage: "Tuition contract link is not available yet.",
      failedMessage: "Unable to open this tuition contract right now.",
    });

  const saveAcceptance = async () => {
    if (!selectedTarget?.childApplicationId) return;
    const currentAcceptanceStep = asRecord(selectedStepData.acceptanceLetter);
    const existingDocumentId = readText(currentAcceptanceStep.documentId);
    const existingDocumentResourceId = readText(
      currentAcceptanceStep.documentResourceId,
    );
    const structuredPayloadSaved = await persistEnrollmentStepData({
      childApplicationId: selectedTarget.childApplicationId,
      step: "step1AcceptanceLetter",
      data: {
        reviewedAt: toUnixSeconds(new Date()),
        acknowledgedBy: "parent",
        notes: acceptanceNotes.trim() || null,
        documentId: existingDocumentId || null,
        documentResourceId: existingDocumentResourceId || null,
      },
    });
    await markStepComplete("step1AcceptanceLetter");
    if (structuredPayloadSaved) {
      const currentChildId = selectedTarget?.childApplicationId ?? "";
      const acceptedNotesToken = readText(acceptanceNotes);
      isAcceptanceDraftDirtyRef.current = false;
      activeAcceptanceChildRef.current = currentChildId;
      lastAcceptanceSyncTokenRef.current = `${currentChildId}|${acceptedNotesToken}`;
    }
    setToastMessage(
      structuredPayloadSaved
        ? "Acceptance letter review recorded."
        : "Acceptance review recorded. Structured payload sync will activate when backend schema parity is refreshed.",
    );
  };

  const createTuitionContractDocument = async (): Promise<{
    documentId: string | null;
    documentResourceId: string;
  }> => {
    if (!selectedTarget?.applicationId || !tuitionContractSnapshot) {
      throw new Error("Tuition contract context is not ready.");
    }
    const sourceDocument = tuitionContractDocuments.find((document) => {
      const category = readText(document.category).toLowerCase();
      const fileName = readText(document.fileName).toLowerCase();
      return (
        readText(document.resourceId) &&
        !category.includes("signed") &&
        !fileName.includes("signed")
      );
    });
    const sourceResourceId = readText(sourceDocument?.resourceId);
    if (!sourceResourceId) {
      throw new Error("The sent tuition contract is not available yet.");
    }

    const signedAt = new Date();
    const sourceUrl = /^https?:\/\//i.test(sourceResourceId)
      ? sourceResourceId
      : (await getUrl({ path: sourceResourceId })).url.toString();
    const sourceResponse = await fetch(sourceUrl);
    if (!sourceResponse.ok) {
      throw new Error("Unable to load the sent tuition contract for signing.");
    }
    const sourceBytes = new Uint8Array(await sourceResponse.arrayBuffer());
    const installmentSummaryLabel = contractPaymentUsesInstallments
      ? readText(selectedTuitionPaymentPlan.previewLabel) || null
      : null;
    const pdfBytes = await appendTuitionContractAcceptancePage({
      originalPdfBytes: sourceBytes,
      signerName: contractSignerName.trim(),
      signatureDataUrl: contractSignature,
      signedAtLabel: signedAt.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      paymentPreferenceLabel: contractPaymentPreferenceLabel,
      paymentAmountLabel: contractPaymentAmountLabel,
      installmentSummaryLabel,
      sourceVersion: readText(selectedTuitionContractStepData.contractVersion),
    });

    const codeToken = toSafeToken(
      readText(selectedApplicationRecord.code) || selectedTarget.applicationId,
    );
    const familyToken = toSafeToken(selectedTarget.familyName) || "family";
    const fileName = `signed-tuition-contract-${codeToken}-${familyToken}.pdf`;
    const uploadedBy = readText(profile?.userSub) || "system";
    const storagePath = buildEnrollmentTuitionContractUploadPath({
      applicationId: selectedTarget.applicationId,
      ownerId: uploadedBy,
      familyName: selectedTarget.familyName,
      fileStem: "signed-tuition-contract",
    });

    let uploaded = false;

    try {
      await uploadData({
        path: storagePath,
        data: new Blob([Uint8Array.from(pdfBytes)], {
          type: "application/pdf",
        }),
        options: {
          contentType: "application/pdf",
          metadata: {
            applicationId: selectedTarget.applicationId,
            childApplicationIds:
              tuitionContractSnapshot.eligibleChildApplicationIds.join(","),
            generatedBy: uploadedBy,
            documentKind: "signed-tuition-contract",
            sourceDocumentId: readText(sourceDocument?.id),
            sourceDocumentResourceId: sourceResourceId,
            sourceContractVersion: readText(
              selectedTuitionContractStepData.contractVersion,
            ),
          },
        },
      }).result;
      uploaded = true;

      const createdDocument = await addAdmissionsDocument({
        applicationId: selectedTarget.applicationId,
        document: {
          resourceId: storagePath,
          category: "tuition_contract_signed",
          uploadedBy,
          fileName,
          contentType: "application/pdf",
          childApplicationIds:
            tuitionContractSnapshot.eligibleChildApplicationIds,
          storageProvider: "s3",
          storageKey: storagePath,
          sizeBytes: pdfBytes.byteLength,
        },
      }).unwrap();

      return {
        documentId: readText(asRecord(createdDocument).id) || null,
        documentResourceId: storagePath,
      };
    } catch (error) {
      if (uploaded) {
        await remove({ path: storagePath }).catch(() => undefined);
      }
      throw error;
    }
  };

  const saveContract = async () => {
    if (!selectedTarget?.childApplicationId) return;
    if (!contractCanSave) return;
    const createdDocument = await createTuitionContractDocument();
    const requestedInstallmentCount = Number.parseInt(
      readText(contractInstallmentCount),
      10,
    );
    const installmentCount = Number.isFinite(requestedInstallmentCount)
      ? requestedInstallmentCount
      : null;
    const structuredPayloadSaved = await persistEnrollmentStepData({
      childApplicationId: selectedTarget.childApplicationId,
      step: "step4TuitionContract",
      data: {
        contractVersion:
          readText(selectedTuitionContractStepData.contractVersion) ||
          admissionSchoolYear,
        signerFullName: contractSignerName.trim() || null,
        signatureToken: contractSignature,
        signedAt: toUnixSeconds(new Date()),
        totalDueCents: tuitionContractSnapshot?.totalObligationCents ?? null,
        paymentMethod: contractPaymentMethod,
        paymentPreferenceLabel: contractPaymentPreferenceLabel,
        paymentAmountLabel: contractPaymentAmountLabel,
        installmentCount,
        installmentAmountLabel: readText(contractInstallmentAmount) || null,
        installmentStartDate: readText(contractInstallmentStartDate) || null,
        installmentEndDate: readText(contractInstallmentEndDate) || null,
        thirdPartyName: readText(contractThirdPartyName) || null,
        documentId: createdDocument.documentId,
        documentResourceId: createdDocument.documentResourceId,
        termsAcknowledged: contractAccepted,
      },
    });
    await markStepComplete("step4TuitionContract");
    await applicationDetailsQuery.refetch();
    if (structuredPayloadSaved) {
      const currentChildId = selectedTarget?.childApplicationId ?? "";
      const signerToken = contractSignerName.trim();
      const acceptedToken = contractAccepted;
      const signatureToken = contractSignature ? 1 : 0;
      const paymentMethodToken = contractPaymentMethod;
      const paymentAmountToken = contractPaymentAmountLabel;
      const installmentCountToken = readText(contractInstallmentCount);
      const installmentAmountToken = readText(contractInstallmentAmount);
      const installmentStartToken = readText(contractInstallmentStartDate);
      const installmentEndToken = readText(contractInstallmentEndDate);
      const thirdPartyNameToken = readText(contractThirdPartyName);
      isContractDraftDirtyRef.current = false;
      activeContractChildRef.current = currentChildId;
      lastContractSyncTokenRef.current = `${currentChildId}|${signerToken}|${acceptedToken ? 1 : 0}|${signatureToken}|${paymentMethodToken}|${paymentAmountToken}|${installmentCountToken}|${installmentAmountToken}|${installmentStartToken}|${installmentEndToken}|${thirdPartyNameToken}`;
    } else {
      isContractDraftDirtyRef.current = true;
    }
    setToastMessage(
      structuredPayloadSaved
        ? "Tuition contract details saved and linked."
        : "Tuition contract document was attached. Structured payload sync will activate when backend schema parity is refreshed.",
    );
  };

  const saveMedical = async (payload: {
    allergies: string[];
    chronicConditions: string[];
    medications: string[];
    immunizationStatus: "up_to_date" | "partial" | "exempt" | "not_provided";
    emergencyNotes: string;
  }) => {
    if (!selectedTarget?.childApplicationId) return;
    try {
      await enrollmentProgress.updateMedicalRecord({
        childApplicationId: selectedTarget.childApplicationId,
        medical: payload,
      });
    } catch (error) {
      if (!isBridgeNotConfiguredError(error)) {
        throw error;
      }
      await updateMedicalRecordMutation({
        childApplicationId: selectedTarget.childApplicationId,
        medical: payload,
      }).unwrap();
    }
    await markStepComplete("step6ImmunizationRecords");
    await Promise.allSettled([
      applicationDetailsQuery.refetch(),
      applicationsQuery.refetch(),
    ]);
    setToastMessage("Medical information saved.");
  };

  const openB6tUploadPicker = () => {
    if (b6tUploading) return;
    setB6tUploadError(null);
    b6tUploadInputRef.current?.click();
  };

  const handleB6tUploadChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (
      !file ||
      !selectedTarget?.applicationId ||
      !selectedTarget.childApplicationId
    )
      return;

    setB6tUploadError(null);
    setToastMessage(null);
    setB6tUploading(true);

    const ownerId = readText(profile?.userSub) || "unknown";
    const storagePath = buildEnrollmentB6tUploadPath({
      applicationId: selectedTarget.applicationId,
      ownerId,
      childName: selectedTarget.childName,
      sourceFileName: file.name,
    });

    let uploaded = false;

    try {
      await uploadData({
        path: storagePath,
        data: file,
        options: {
          contentType: file.type || "application/octet-stream",
          metadata: {
            applicationId: selectedTarget.applicationId,
            childApplicationId: selectedTarget.childApplicationId,
            uploadedBy: ownerId,
            documentName: "b6t_transport_form",
          },
        },
      }).result;
      uploaded = true;

      await addAdmissionsDocument({
        applicationId: selectedTarget.applicationId,
        document: {
          resourceId: storagePath,
          category: "other",
          uploadedBy: ownerId,
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          childApplicationIds: [selectedTarget.childApplicationId],
          storageProvider: "s3",
          storageKey: storagePath,
        },
      }).unwrap();

      await markStepComplete("step3B6TForm");
      await applicationDetailsQuery.refetch();
      setToastMessage("B6T transport form uploaded.");
    } catch (error) {
      if (uploaded) {
        await remove({ path: storagePath }).catch(() => undefined);
      }
      const message =
        collectErrorMessages(error).find(Boolean) ||
        "Unable to upload the B6T form right now.";
      setB6tUploadError(message);
    } finally {
      setB6tUploading(false);
    }
  };

  return (
    <main
      data-testid="parent-portal-enrollment-status"
      data-extracted-from="owned:ystnj/parent-portal/enrollment-status"
      className="w-full space-y-5"
    >
      {toastMessage ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-emerald-800">
            {toastMessage}
          </p>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="text-xs font-bold text-emerald-800"
          >
            Dismiss
          </button>
        </section>
      ) : null}

      {b6tUploadError ? (
        <section className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-red-800">{b6tUploadError}</p>
          <button
            type="button"
            onClick={() => setB6tUploadError(null)}
            className="text-xs font-bold text-red-800"
          >
            Dismiss
          </button>
        </section>
      ) : null}

      {!applicantId ? (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
          Account context is required to render enrollment scope.
        </section>
      ) : null}

      {applicationsQuery.isLoading ? (
        <section className="rounded-2xl border border-gray-200 bg-white px-4 py-6 flex items-center gap-2 text-sm text-irongrey/70">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading enrollment routes from live data.
        </section>
      ) : null}

      {applicationsQuery.error ? (
        <section className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">
          Enrollment data request failed for this session.
        </section>
      ) : null}

      {!applicationsQuery.isLoading &&
      !applicationsQuery.error &&
      enrollmentTargets.length === 0 ? (
        <section className="rounded-2xl border border-gray-200 bg-white px-5 py-6 space-y-1">
          <p className="text-sm font-semibold text-irongrey">
            Enrollment checklist will appear after an accepted admissions
            decision is recorded.
          </p>
          <p className="text-xs text-irongrey/70">
            This screen is live-data driven and updates as application status
            changes.
          </p>
        </section>
      ) : null}

      {shouldRenderTargetList ? (
        <section className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-sticky-last-action-col w-full min-w-[760px] border-collapse">
              <caption className="sr-only">Enrollment-ready students</caption>
              <thead>
                <tr className="bg-gray-50/70 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">
                  <th className="px-6 py-4 text-left">Family</th>
                  <th className="px-6 py-4 text-left">Student</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left w-[180px] min-w-[180px]">
                    Application Code
                  </th>
                  <th className="px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {enrollmentTargets.map((target) => {
                  const isSelectedTarget =
                    target.applicationId === selectedTarget?.applicationId &&
                    target.childApplicationId ===
                      selectedTarget?.childApplicationId;
                  const targetProgress = isSelectedTarget
                    ? selectedProgress
                    : buildEnrollmentProgressSnapshot(
                        target.enrollmentProgress,
                        completedStepOverrides[target.childApplicationId],
                      );
                  const isAllStepsComplete =
                    isAllEnrollmentStepsComplete(targetProgress);
                  const targetAction = resolveEnrollmentTargetAction({
                    target,
                    progress: targetProgress,
                    allStepsComplete: isAllStepsComplete,
                  });
                  const targetActionClassName = isAllStepsComplete
                    ? "inline-flex min-w-[142px] items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700"
                    : targetAction.registrationFormComplete
                      ? "inline-flex min-w-[142px] items-center justify-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800"
                      : "inline-flex items-center justify-center gap-1.5 rounded-xl bg-jungle px-3 py-2 text-xs font-bold text-white";
                  return (
                    <tr
                      key={`${target.applicationId}:${target.childApplicationId}`}
                      className="border-t border-gray-100"
                      data-testid="parent-portal-enrollment-select-row"
                    >
                      <td className="px-6 py-4 text-sm font-semibold text-irongrey">
                        {target.familyName}
                      </td>
                      <td className="px-6 py-4 text-sm text-irongrey">
                        {target.childName}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <EnrollmentStatusBadge status={target.childStatus} />
                          {targetAction.statusHint ? (
                            <p
                              className={`text-[11px] font-semibold ${isAllStepsComplete ? "text-emerald-700" : "text-amber-700"}`}
                            >
                              {targetAction.statusHint}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td className="w-[180px] min-w-[180px] px-6 py-4 text-xs text-irongrey/70 whitespace-nowrap">
                        {target.applicationCode}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={targetAction.href}
                          data-testid="parent-portal-enrollment-continue"
                          className={targetActionClassName}
                        >
                          {isAllStepsComplete ? (
                            <CircleCheckBig className="h-3.5 w-3.5" />
                          ) : null}
                          {targetAction.label}
                          {isAllStepsComplete ? null : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {selectedTarget ? (
        <section className="space-y-5">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-jungle">
                  Enrollment Checklist
                </h2>
                <p className="text-sm text-irongrey/70">
                  {selectedTarget.childName} · Application{" "}
                  {selectedTarget.applicationCode}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-irongrey/50">
                  Deadline
                </p>
                <p className="text-sm font-semibold text-irongrey">
                  {formatUnixDate(selectedTarget.enrollmentDeadline)}
                </p>
                {typeof daysRemaining === "number" ? (
                  <p
                    className={`text-xs font-semibold ${daysRemaining <= 7 ? "text-amber-700" : "text-jungle"}`}
                  >
                    {daysRemaining >= 0
                      ? `${daysRemaining} days remaining`
                      : "Deadline elapsed"}
                  </p>
                ) : null}
              </div>
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs font-semibold text-irongrey/70">
                <span>Progress</span>
                <span>
                  {completedCount} / {STEPS.length} complete
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100">
                <div
                  className="h-2 rounded-full bg-jungle transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>

          {selectedStep === "checklist" ? (
            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
              {STEPS.map((step, index) => {
                const done =
                  step.key === "payment"
                    ? paymentStepDone
                    : step.key === "b6t"
                      ? b6tStepDone
                      : step.key === "registration"
                        ? selectedRegistrationFormComplete
                        : readEnrollmentProgressFlag(
                            selectedProgress[step.progressKey],
                          );
                const Icon = step.icon;
                return (
                  <div
                    key={step.id}
                    className={`flex flex-wrap items-center gap-4 px-4 py-4 ${index < STEPS.length - 1 ? "border-b border-gray-100" : ""}`}
                  >
                    <div
                      className={`h-10 w-10 rounded-xl flex items-center justify-center ${done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-irongrey/60"}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <p className="text-sm font-semibold text-irongrey">
                        {step.title}
                      </p>
                      {step.key === "b6t" ? (
                        <>
                          <a
                            href={B6T_FORM_DOWNLOAD_HREF}
                            download
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold text-primary underline underline-offset-2 hover:text-primary-hover"
                          >
                            Download the B6T Transport Form PDF
                          </a>
                          {b6tDocuments.length ? (
                            <p className="mt-1 text-xs text-irongrey/60">
                              Latest upload:{" "}
                              {readText(b6tDocuments[0]?.fileName) ||
                                "B6T form"}{" "}
                              ({formatUnixDate(b6tDocuments[0]?.uploadedAt)})
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <p className="text-xs text-irongrey/60">{step.desc}</p>
                      )}
                    </div>
                    {step.key === "acceptance" ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (acceptanceDocuments.length > 0) {
                            void openAcceptanceDocument(
                              acceptanceDocuments[0],
                              0,
                            );
                            return;
                          }
                          jumpToStep("acceptance");
                        }}
                        className="inline-flex w-[131px] items-center justify-center gap-1.5 rounded-xl bg-jungle px-3 py-2 text-xs font-bold text-white"
                      >
                        <CircleCheckBig className="h-3.5 w-3.5" />
                        View
                      </button>
                    ) : step.key === "b6t" ? (
                      done ? (
                        <button
                          type="button"
                          disabled
                          className="inline-flex w-[131px] items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 disabled:opacity-100"
                        >
                          <CircleCheckBig className="h-3.5 w-3.5" />
                          Complete
                        </button>
                      ) : (
                        <>
                          <input
                            ref={b6tUploadInputRef}
                            type="file"
                            accept="application/pdf,image/*"
                            className="hidden"
                            onChange={(event) => {
                              void handleB6tUploadChange(event);
                            }}
                            data-testid="parent-portal-enrollment-b6t-upload-input"
                          />
                          <button
                            type="button"
                            onClick={openB6tUploadPicker}
                            disabled={isPersisting}
                            className="inline-flex w-[131px] items-center justify-center gap-1.5 rounded-xl bg-jungle px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                            data-testid="parent-portal-enrollment-b6t-upload-button"
                          >
                            {b6tUploading ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <IconUpload size={14} stroke={2} />
                            )}
                            Upload
                          </button>
                        </>
                      )
                    ) : step.key === "registration" ? (
                      <Link
                        to={buildEnrollmentIntakeLink(
                          selectedTarget,
                          done ? { editRegistration: true } : {},
                        )}
                        className={`inline-flex w-[142px] items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold ${done ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : "bg-jungle text-white"}`}
                      >
                        {done ? (
                          <CircleCheckBig className="h-3.5 w-3.5" />
                        ) : null}
                        {done ? "Review / Edit" : "Complete Form"}
                        {done ? null : <ChevronRight className="h-3.5 w-3.5" />}
                      </Link>
                    ) : done ? (
                      <ChecklistCompleteBadge />
                    ) : (
                      <button
                        type="button"
                        onClick={() => jumpToStep(step.key)}
                        className="inline-flex w-[131px] items-center justify-center gap-1.5 rounded-xl bg-jungle px-3 py-2 text-xs font-bold text-white"
                      >
                        {getChecklistActionLabel(step.key, done)}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}

          {selectedStep === "acceptance" ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <button
                type="button"
                onClick={jumpToChecklist}
                className="inline-flex items-center gap-1 text-sm font-semibold text-jungle"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to checklist
              </button>
              <h3 className="text-lg font-bold text-jungle">
                Acceptance Letter
              </h3>
              <p className="text-sm text-irongrey/70">
                Review acceptance details and record acknowledgment for this
                student.
              </p>
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                Student: <strong>{selectedTarget.childName}</strong> · Grade
                applying:{" "}
                <strong>
                  {readText(selectedChild.gradeApplyingFor) || "N/A"}
                </strong>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide text-irongrey/50 mb-2">
                  Acceptance Document
                </p>
                {acceptanceDocuments.length ? (
                  <ul className="space-y-2">
                    {acceptanceDocuments.map((document, index) => {
                      const resourceId = readText(document.resourceId);
                      const documentId =
                        readText(document.id) || `acceptance-${index}`;
                      const fileName =
                        readText(document.fileName) ||
                        `Acceptance document ${index + 1}`;
                      const openedAt = formatUnixDate(document.uploadedAt);
                      const opening = openingAcceptanceDocId === documentId;
                      const canOpen = Boolean(resourceId);
                      return (
                        <li
                          key={`${resourceId || fileName}-${index}`}
                          className="flex flex-wrap items-center justify-between gap-2 text-sm"
                        >
                          <span className="text-irongrey">
                            {fileName}{" "}
                            <span className="text-irongrey/60">
                              ({openedAt})
                            </span>
                          </span>
                          <button
                            type="button"
                            disabled={opening || !canOpen}
                            onClick={() => {
                              void openAcceptanceDocument(document, index);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-bold text-jungle disabled:opacity-60"
                          >
                            {opening ? "Opening..." : "Open Document"}
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-irongrey/70">
                    Acceptance document linkage is pending in admissions records
                    for this student.
                  </p>
                )}
              </div>
              <label className="text-sm font-semibold text-irongrey space-y-1 block">
                Acknowledgment Notes
                <textarea
                  value={acceptanceNotes}
                  onChange={(event) => {
                    isAcceptanceDraftDirtyRef.current = true;
                    setAcceptanceNotes(event.target.value);
                  }}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm min-h-[110px]"
                  placeholder="Optional notes for acceptance acknowledgment"
                />
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={isPersisting}
                  onClick={saveAcceptance}
                  className="inline-flex items-center gap-2 rounded-xl bg-jungle px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                >
                  {isPersisting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Save Acceptance Review
                </button>
              </div>
            </div>
          ) : null}

          {selectedStep === "registration" ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <button
                type="button"
                onClick={jumpToChecklist}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-xs font-bold text-irongrey"
              >
                <ArrowLeftToLine className="h-4 w-4" />
                Checklist
              </button>
              <h3 className="text-lg font-bold text-jungle">
                Registration Form
              </h3>
              <p className="text-sm text-irongrey/70">
                This step uses the existing intake enrollment form runtime.
              </p>
              <Link
                to={buildEnrollmentIntakeLink(
                  selectedTarget,
                  selectedRegistrationFormComplete
                    ? { editRegistration: true }
                    : {},
                )}
                className="inline-flex items-center gap-1.5 rounded-xl bg-jungle px-4 py-2 text-sm font-bold text-white"
              >
                {selectedRegistrationFormComplete
                  ? "Review / edit registration form"
                  : "Complete form in intake flow"}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          ) : null}

          {selectedStep === "contract" ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <button
                type="button"
                onClick={jumpToChecklist}
                className="inline-flex items-center gap-1 text-sm font-semibold text-jungle"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to checklist
              </button>
              <h3 className="text-lg font-bold text-jungle">
                Tuition Contract
              </h3>
              <p className="text-sm text-irongrey/75">
                Existing signed timestamp:{" "}
                <strong>
                  {formatUnixDate(selectedTuitionContractStepData.signedAt)}
                </strong>
              </p>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide text-irongrey/50 mb-2">
                  Linked Contract Document
                </p>
                {tuitionContractDocuments.length ? (
                  <ul
                    data-testid="parent-enrollment-tuition-contract-documents"
                    className="space-y-2"
                  >
                    {tuitionContractDocuments.map((document, index) => {
                      const resourceId = readText(document.resourceId);
                      const documentId =
                        readText(document.id) || `tuition-contract-${index}`;
                      const fileName =
                        readText(document.fileName) ||
                        `Tuition contract ${index + 1}`;
                      const opening = openingContractDocId === documentId;
                      return (
                        <li
                          key={`${resourceId || fileName}-${index}`}
                          data-testid="parent-enrollment-tuition-contract-document-row"
                          className="flex flex-wrap items-center justify-between gap-2 text-sm"
                        >
                          <span className="text-irongrey">
                            {fileName}{" "}
                            <span className="text-irongrey/60">
                              ({formatUnixDate(document.uploadedAt)})
                            </span>
                          </span>
                          <button
                            type="button"
                            disabled={opening || !resourceId}
                            data-testid="parent-enrollment-tuition-contract-open-button"
                            onClick={() => {
                              void openTuitionContractDocument(document, index);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-bold text-jungle disabled:opacity-60"
                          >
                            {opening ? "Opening..." : "Open Document"}
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-irongrey/70">
                    The school will send the tuition contract before this step
                    can be completed.
                  </p>
                )}
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 space-y-3">
                <p className="text-sm font-bold text-irongrey">
                  Family Contract Summary
                </p>
                <p className="text-sm text-irongrey/80">
                  School year: <strong>{admissionSchoolYear}</strong>
                </p>
                <p className="text-sm text-irongrey/80">
                  Review the amounts from the tuition contract sent by the
                  school.
                </p>
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-irongrey/50">
                    Eligible Students
                  </p>
                  {tuitionContractSnapshot?.studentRows.length ? (
                    <ul className="space-y-1">
                      {tuitionContractSnapshot.studentRows.map((row) => (
                        <li
                          key={`${row.childName}-${row.gradeLabel}`}
                          className="flex items-center justify-between rounded-lg border border-white bg-white px-3 py-2 text-sm text-irongrey"
                        >
                          <span>
                            {row.childName} · {row.gradeLabel}
                          </span>
                          <span className="font-semibold">
                            {row.tuitionLabel}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-irongrey/70">
                      Tuition contract details will appear after the school
                      sends the approved contract.
                    </p>
                  )}
                </div>
                {tuitionContractSnapshot ? (
                  <>
                    <div className="space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-irongrey/50">
                        Additional Fees
                      </p>
                      {tuitionContractSnapshot.additionalFees.length ? (
                        <ul className="space-y-1">
                          {tuitionContractSnapshot.additionalFees.map((fee) => (
                            <li
                              key={fee.label}
                              className="rounded-lg border border-white bg-white px-3 py-2 text-sm text-irongrey"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span>{fee.label}</span>
                                <span className="font-semibold">
                                  {fee.totalLabel}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-irongrey/60">
                                {fee.scheduleLabel}
                              </p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-irongrey/70">
                          No additional fee schedule is configured.
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2 rounded-lg border border-white bg-white px-3 py-3 text-sm text-irongrey sm:grid-cols-2">
                      <p>
                        Tuition subtotal:{" "}
                        <strong>
                          {formatCurrencyLabel(
                            tuitionContractSnapshot.tuitionSubtotalCents,
                          )}
                        </strong>
                      </p>
                      <p>
                        Total fees:{" "}
                        <strong>
                          {formatCurrencyLabel(
                            tuitionContractSnapshot.totalFeesCents,
                          )}
                        </strong>
                      </p>
                      <p>
                        Total obligation:{" "}
                        <strong>
                          {formatCurrencyLabel(
                            tuitionContractSnapshot.totalObligationCents,
                          )}
                        </strong>
                      </p>
                      <p>
                        Registration fee step:{" "}
                        <strong>
                          {schoolPublicInfo.registrationFee.descriptionLabel}
                        </strong>
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
              {tuitionContractSnapshot ? (
                <>
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-4 space-y-4">
                    <div>
                      <p className="text-sm font-bold text-irongrey">
                        Payment Preference
                      </p>
                      <p className="text-xs text-irongrey/60">
                        These details are included with your signed contract
                        record.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="text-sm font-semibold text-irongrey space-y-1">
                        Payment Method
                        <select
                          value={contractPaymentMethod}
                          onChange={(event) => {
                            isContractDraftDirtyRef.current = true;
                            setContractPaymentMethod(
                              event.target
                                .value as TuitionContractPaymentMethod,
                            );
                          }}
                          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                        >
                          {!contractPaymentUsesInstallments ? (
                            <option value="full">Payment in full</option>
                          ) : null}
                          <option value="electronic">
                            Equal electronic payments
                          </option>
                          <option value="post_dated_checks">
                            Equal post-dated checks
                          </option>
                          <option value="third_party_checks">
                            Third-party checks
                          </option>
                        </select>
                      </label>
                      <label className="text-sm font-semibold text-irongrey space-y-1">
                        Payment Amount
                        <input
                          type="text"
                          value={contractPaymentAmount}
                          readOnly
                          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                          placeholder={formatCurrencyLabel(
                            tuitionContractSnapshot?.totalObligationCents ?? 0,
                          )}
                        />
                      </label>
                    </div>
                    {contractPaymentUsesInstallments ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        <label className="text-sm font-semibold text-irongrey space-y-1">
                          Number of Payments
                          <input
                            type="number"
                            min="1"
                            value={contractInstallmentCount}
                            readOnly
                            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                            placeholder="10"
                          />
                        </label>
                        <label className="text-sm font-semibold text-irongrey space-y-1">
                          Amount Per Payment
                          <input
                            type="text"
                            value={contractInstallmentAmount}
                            readOnly
                            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                            placeholder="$1,250"
                          />
                        </label>
                        <label className="text-sm font-semibold text-irongrey space-y-1">
                          First Payment Date
                          <input
                            type="date"
                            value={contractInstallmentStartDate}
                            readOnly
                            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                          />
                        </label>
                        <label className="text-sm font-semibold text-irongrey space-y-1">
                          Last Payment Date
                          <input
                            type="date"
                            value={contractInstallmentEndDate}
                            readOnly
                            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                          />
                        </label>
                      </div>
                    ) : null}
                    {contractPaymentUsesThirdParty ? (
                      <label className="text-sm font-semibold text-irongrey space-y-1 block">
                        Third Party Name
                        <input
                          type="text"
                          value={contractThirdPartyName}
                          onChange={(event) => {
                            isContractDraftDirtyRef.current = true;
                            setContractThirdPartyName(event.target.value);
                          }}
                          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                          placeholder="Third-party payer name"
                        />
                      </label>
                    ) : null}
                  </div>
                  <label className="text-sm font-semibold text-irongrey space-y-1">
                    Signer Name
                    <input
                      type="text"
                      value={contractSignerName}
                      onChange={(event) => {
                        isContractDraftDirtyRef.current = true;
                        setContractSignerName(event.target.value);
                      }}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-primary"
                      placeholder="Parent/Guardian full name"
                    />
                  </label>
                  <DigitalSignaturePad
                    label="Digital Signature"
                    value={contractSignature}
                    required
                    testId="parent-enrollment-tuition-contract-signature-canvas"
                    onBeginEdit={() => {
                      isContractDraftDirtyRef.current = true;
                    }}
                    onChange={(nextValue) => {
                      isContractDraftDirtyRef.current = true;
                      setContractSignature(nextValue);
                    }}
                  />
                  <label
                    htmlFor="contract-terms-checkbox"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-irongrey cursor-pointer"
                  >
                    <input
                      id="contract-terms-checkbox"
                      type="checkbox"
                      checked={contractAccepted}
                      onChange={(event) => {
                        isContractDraftDirtyRef.current = true;
                        setContractAccepted(event.target.checked);
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
                    />
                    I acknowledge the tuition contract terms.
                  </label>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={isPersisting || !contractCanSave}
                      onClick={saveContract}
                      data-testid="parent-enrollment-tuition-contract-save-button"
                      className="inline-flex items-center gap-2 rounded-xl bg-jungle px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                    >
                      {isPersisting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                      Save Contract Details
                    </button>
                  </div>
                </>
              ) : (
                <div
                  data-testid="parent-enrollment-tuition-contract-pending"
                  className="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm text-irongrey/70"
                >
                  The school is still preparing this tuition contract. Signature
                  and payment preference fields will unlock after the approved
                  contract is sent.
                </div>
              )}
            </div>
          ) : null}

          {selectedStep === "medical" ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <button
                type="button"
                onClick={jumpToChecklist}
                className="inline-flex items-center gap-1 text-sm font-semibold text-jungle"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to checklist
              </button>
              <h3 className="text-lg font-bold text-jungle">
                Medical Information
              </h3>
              <MedicalEditor
                initialMedical={asRecord(selectedChild.medical)}
                onSave={saveMedical}
                saving={isPersisting}
              />
            </div>
          ) : null}

          {selectedStep === "payment" ? (
            <div
              className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4"
              data-testid="parent-portal-enrollment-payment-step"
            >
              <button
                type="button"
                onClick={jumpToChecklist}
                className="inline-flex items-center gap-1 text-sm font-semibold text-jungle"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to checklist
              </button>
              <h3 className="text-lg font-bold text-jungle">
                Registration Payment
              </h3>
              <p className="text-sm text-irongrey/75">
                Submit the live registration fee configured by the school for
                this student.
              </p>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-irongrey/60">
                      Registration Fee
                    </p>
                    <p
                      className="mt-1 text-2xl font-bold text-jungle"
                      data-testid="parent-portal-enrollment-registration-fee-amount"
                    >
                      {registrationFeeAmountLabel}
                    </p>
                    <p className="mt-1 text-xs text-irongrey/70">
                      Charged {registrationFeeScopeLabel} for{" "}
                      {selectedTarget?.childName || "this student"}.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="parent-portal-enrollment-payment-button"
                    disabled={
                      paymentCheckoutLaunching ||
                      registrationFeeAmountCents <= 0
                    }
                    onClick={() => {
                      void startRegistrationPaymentCheckout();
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-jungle px-4 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {paymentCheckoutLaunching ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    {paymentCheckoutLaunching
                      ? "Redirecting to Stripe…"
                      : "Pay Registration Fee"}
                  </button>
                </div>
              </div>
              {paymentStatusFromUrl && !paymentStepDone ? (
                <p
                  className="text-sm font-semibold text-amber-700"
                  data-testid="parent-portal-enrollment-payment-sync"
                >
                  Stripe returned successfully. Confirming payment with the
                  school record now.
                </p>
              ) : null}
              {paymentCanceledFromUrl ? (
                <p
                  className="text-sm font-semibold text-amber-700"
                  data-testid="parent-portal-enrollment-payment-canceled"
                >
                  Payment was canceled before completion. You can retry when
                  ready.
                </p>
              ) : null}
              {paymentCheckoutError ? (
                <SupportErrorMessage data-testid="parent-portal-enrollment-payment-error">
                  {paymentCheckoutError}
                </SupportErrorMessage>
              ) : null}
              {paymentStepDone ? (
                <p
                  className="text-sm font-semibold text-emerald-700"
                  data-testid="parent-portal-enrollment-payment-complete"
                >
                  Payment detected. This checklist item is marked complete.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-[2fr,1fr]">
            <section className="rounded-2xl border border-gray-200 bg-white p-4">
              <h4 className="text-xs font-bold uppercase tracking-wide text-irongrey/50 mb-2">
                Support
              </h4>
              <p className="text-sm text-irongrey/80">Admissions Office</p>
              {schoolPublicInfo.schoolPhoneDisplay ? (
                <p className="text-sm font-semibold text-jungle">
                  {schoolPublicInfo.schoolPhoneDisplay}
                </p>
              ) : schoolPublicInfo.schoolEmail ? (
                <p className="text-sm font-semibold text-jungle">
                  {schoolPublicInfo.schoolEmail}
                </p>
              ) : null}
              <p className="mt-2 text-xs text-irongrey/60 inline-flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Live data updates as admissions records change.
              </p>
            </section>
          </div>
        </section>
      ) : null}
    </main>
  );
}
